
import { Component, Prop, Ref, Vue, Watch, PropSync } from "vue-property-decorator"
import { Getter } from "vuex-class"

// hugerte imports
import hugerte, { Editor, RawEditorOptions } from "hugerte"
import "hugerte/icons/default"
import "hugerte/themes/silver"
import "hugerte/skins/ui/oxide/skin.css"
import "hugerte/plugins/advlist"
import "hugerte/plugins/autolink"
import "hugerte/plugins/fullscreen"
import "hugerte/plugins/image"
import "hugerte/plugins/lists"
import "hugerte/plugins/link"
import "hugerte/plugins/preview"
import "hugerte/plugins/searchreplace"
import "hugerte/plugins/table"
import "hugerte/plugins/pagebreak"
import "hugerte/plugins/anchor"
import "hugerte/models/dom"
import "hugerte/plugins/visualblocks"
import "hugerte/plugins/code"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SearchDropDown from "@/components/Controls/SearchDropDown.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import Placeholder from "@/utils/Placeholder"
import { getStylesheetName } from "@/utils/ThemeLoader"
import DataObjectSearchDropdown from "../Controls/DataObjectSearchDropdown.vue"
import { FormatMargin, FormatStyle } from "@/types/format"

	@Component({
		components: {
			"scrollbar": VuePerfectScrollbar,
			"search-dropdown": SearchDropDown,
			"data-object-search-dropdown": DataObjectSearchDropdown
		}
	})

export default class PdfRichTextEditor extends Vue {
	@Ref() editorContainer!: HTMLTextAreaElement;

	// Store
	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	// Props
	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ default: null })
	public minHeight!: string | null;

	@Prop({ type: Boolean, default: false })
	public userHistoryOnly!: boolean;

	@Prop({ type: Boolean, default: false })
	public applyMaxHeight!: boolean;

	@Prop({ type: Array, default: () => null })
	public placeholders!: { id: string, label: string }[] | null;

	@PropSync("sections", { type: Array, default: () => [] })
	public syncedSections!: { id: string, label: string } [];

	@PropSync("dataObjectPlaceholders", { type: Array, default: () => [] })
	public syncedDataObjectPlaceholders!: { id: string, label: string } [];

	@Prop({ type: Boolean, default: false })
	public isPrintTemplate!: boolean;

	@Prop({ default: null })
	public caseHeader!: string | null;

	@Prop({ default: null })
	private formatFooter!: string | null;

	@Prop({ default: null })
	public formatMargin!: FormatMargin | null;

	@Prop({ default: null })
	public formatStyles!: FormatStyle | null;

	// Data
	private editor: Editor | null = null;
	private focusWhenReady: boolean = false;
	private hasPageNumbers: boolean = this.formatFooter !== null && this.formatFooter !== "";
	private caseHeaderEnabled: boolean = this.caseHeader !== null && this.caseHeader !== "";
	private marginModalOpen: boolean = false;
	// Source of truth for the current margins
	private currentMargin: FormatMargin = {
		top: 25,
		right: 25,
		bottom: 25,
		left: 25
	};
	// Margins to use when editing to ensure they don't overwrite existing margins until saved
	private editingMargin: FormatMargin = {
		top: 25,
		right: 25,
		bottom: 25,
		left: 25
	};
	// Editor font formats
	private currentFormatStyles: FormatStyle = {
		fontSize: "10pt",
		fontName: "Helvetica,Arial,sans-serif"
	}

	// Editor Options
	// HugeRTE plugins
	private plugins: string[] =
	[
		"advlist", "autolink", "fullscreen", "image", "lists", "link", "searchreplace", "table", "pagebreak", "anchor", "visualblocks"
	];

	// Specific styles to be passed into the HugeRTE editor.
	private get editorStyles (): string {
		return `
			body
			{
				font-family:${this.currentFormatStyles.fontName};
				font-size: ${this.currentFormatStyles.fontSize};
				margin-top: ${this.currentMargin.top}mm;
				margin-left: ${this.currentMargin.left}mm;
				margin-bottom: ${this.currentMargin.bottom}mm;
				margin-right: ${this.currentMargin.right}mm;
				background-color: white !important;
			}
			.mce-offscreen-selection
			{
				display: none;
			}
			.mce-pagebreak {
				width: 100%;
				height: 1px;
				display: block;
				border-top: 1px dashed darkgray;
			}
		`
	};

	// Getters
	private get minHeightStyleString (): string {
		return `min-height: ${this.minHeight};`
	}

	private get isPdfTemplateHeaderEnabled () : boolean {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplateHeaderAndFooter)
	}

	private get stylesheet () : string {
		const isDarkMode: boolean = window.matchMedia("(prefers-color-scheme: dark)").matches
		return getStylesheetName(isDarkMode, false)
	}

	private get marginsValid (): boolean {
		// Must check if the type is a number as inputting a minus symbol "-" is a string and will pass validation without type check
		return (typeof this.editingMargin.top === "number" && this.editingMargin.top >= 0 && this.editingMargin.top <= 100) &&
						(typeof this.editingMargin.left === "number" && this.editingMargin.left >= 0 && this.editingMargin.left <= 100) &&
						(typeof this.editingMargin.right === "number" && this.editingMargin.right >= 0 && this.editingMargin.right <= 100) &&
						(typeof this.editingMargin.bottom === "number" && this.editingMargin.bottom >= 0 && this.editingMargin.bottom <= 100)
	}

	private handleFormatUpdate (event: any): void {
		let selectedContent = this.editor?.selection.getContent()
		if (event && !selectedContent) {
			switch (event.format) {
				case "fontsize":
					this.currentFormatStyles.fontSize = event.vars.value
					break
				case "fontname":
					this.currentFormatStyles.fontName = event.vars.value
					break
			}

			this.updateEditorStyle()
			this.$emit("setCssOverrides", this.currentFormatStyles)
		}
	}

	// HugeRTE setup function to be run when the editor is first initialised.
	private onEditorSetup (editor: Editor) : void {
		editor.on("input", () => this.$emit("input", editor.getContent()))

		// when a user uses a button emit the content up.
		editor.on("ExecCommand", () => this.$emit("input", editor.getContent()))

		// when a user applies a format, update if necessary
		editor.on("FormatApply", (e) => this.handleFormatUpdate(e))

		// this resolves an issue where you cannot interact with the HugeRTE dialogues within BModal.
		document.addEventListener("focusin", function (e) {
			let target = e.target as Element
			let closest = target.closest(".tox-hugerte-aux, .tox-dialog, .moxman-window, .tam-assetmanager-root")
			if (closest !== null && closest !== undefined) {
				e.stopImmediatePropagation()
			}
		})

		// add the custom buttons
		if (this.isPrintTemplate) {
			editor.ui.registry.addButton("includeMargins", {
				text: "Margin",
				onAction: this.openMarginSetup
			})

			if (this.isPdfTemplateHeaderEnabled) {
				editor.ui.registry.addToggleButton("includeHeader",
					{
						text: "Header",
						onAction: this.onIncludeHeaderClick,
						onSetup: this.onIncludeHeaderSetup
					})

				editor.ui.registry.addToggleButton("usePageNumbers",
					{
						text: "Footer",
						onAction: this.onUsePageNumbersClick,
						onSetup: this.onUsePageNumbersSetup
					})
			}
		}
	}

	// Initialise HugeRTE editor on Mounted.
	private async initialiseHugeRTE () : Promise<void> {
		const hugeRteConfig: RawEditorOptions =
		{
			target: this.editorContainer,
			height: this.minHeight || 550,
			resize: false,
			plugins: this.plugins,
			toolbar: "undo redo | bold italic forecolor | alignleft aligncenter alignright alignjustify pagebreak | bullist numlist | link image table | includeHeader usePageNumbers includeMargins",
			content_style: this.editorStyles,
			promotion: false,
			visualblocks_default_state: true,
			pagebreak_separator: "<div class=\"page-break\" style=\"page-break-before: always; clear:both\"/></div>",
			content_css: [this.stylesheet],
			noneditable_class: "ql-placeholder-content",
			menubar: "edit insert view format table",
			setup: this.onEditorSetup
		}

		this.editor = (await hugerte.init(hugeRteConfig))[0]
	}

	// Function to be run when Include Header button is initialised.
	private onIncludeHeaderSetup (editor: any) {
		editor.setActive(this.isPrintTemplate && this.isPdfTemplateHeaderEnabled && this.caseHeaderEnabled)
		return (editor: any) => {
			// on destroy
		}
	}

	// Function to be run when Use Page Numbers button is initialised.
	private onUsePageNumbersSetup (editor: any) {
		editor.setActive(this.isPrintTemplate && this.isPdfTemplateHeaderEnabled && this.hasPageNumbers)
		return (editor: any) => {
			// on destroy.
		}
	}

	// Methods
	focusOut (event: FocusEvent) : void {
		// Ensure that this event isn't triggered when changing between children, e.g. clicking a button while focused on text
		if (!event.relatedTarget || !this.$el.contains(event.relatedTarget as Element)) {
			this.$emit("blur")
		}
	}

	clickAndFocus (): void {
		if (this.readonly) {
			this.focusWhenReady = true
			this.$emit("focus")
		}
	}

	private closeMarginModal (): void {
		this.marginModalOpen = false
	}

	private setupStyles (): void {
		if (this.formatMargin != null) {
			this.currentMargin = { ...this.formatMargin }
		}

		if (this.formatStyles != null) {
			this.currentFormatStyles = { ...this.formatStyles }
		}
	}

	private openMarginSetup (): void {
		if (this.formatMargin != null) {
			this.editingMargin = { ...this.currentMargin }
		}

		this.marginModalOpen = true
	}

	/**
	 * Adds the selected placeholder at the cursor location
	 * @param placeholder The placeholder to add, with the id being the field id or built in property name and the label being what is displayed to the users.
	 * @private
	 */
	private addPlaceholder (placeholder: { id: string, label: string }, prefix: string = "Field", placeholderColourClass: string = "badge-primary") : void {
		let label = placeholder.label

		if (prefix) {
			label = `${prefix}: ${label}`
		}

		let content: string = Placeholder.getPlaceholderHtml(placeholder.id, label, placeholderColourClass)

		this.editor!.insertContent(content)
		this.$emit("input", this.editor?.getContent())
	}

	/**
		 * Adds a section placeholder with a "Section:" prefix at the cursor location.
		 * @param placeholder The placeholder to add, with the id being the field id or built in property name and the label being what is displayed to the users.
		 */
	private addSectionPlaceholder (placeholder: { id: string, label: string }): void {
		this.addPlaceholder(placeholder, "Section", "badge-danger")
	}

	private addDataObjectPlaceholder (placeholder: { id: string, label: string }): void {
		this.addPlaceholder(placeholder, "Data Object", "badge-warning")
	}

	/**
	 * Creates a custom Section placeholder with a "Section:" prefix at the cursor location.
	 * @param label The label of the custom section placeholder, used to generate a placeholder id and is displayed to the user prefixed with "Section: "
	 */
	private addCustomSectionPlaceholder (label: string): void{
		let placeholder = { id: `section.${label}`, label: label }
		this.syncedSections.push(placeholder)
		this.addSectionPlaceholder(placeholder)
	}

	private onIncludeHeaderClick (editor: any) : void {
		this.caseHeaderEnabled = !this.caseHeaderEnabled
		editor.setActive(this.caseHeaderEnabled)
		this.$emit("includeCaseHeaderChange", this.caseHeaderEnabled)
	}

	private onUsePageNumbersClick (editor: any): void{
		this.hasPageNumbers = !this.hasPageNumbers
		editor.setActive(this.hasPageNumbers)
		this.$emit("setUsePageNumbers", this.hasPageNumbers)
	}

	private onMarginInput (editor: any): void{
		this.marginModalOpen = !this.marginModalOpen

		this.currentMargin = { ...this.editingMargin }
		this.$emit("setFormatMargin", this.currentMargin)

		// Update the editor margins when margin modal closes
		this.updateEditorStyle()
	}

	private updateEditorStyle (): void {
		if (this.editor) {
			this.editor.iframeElement!.contentDocument!.getElementsByTagName("style")[0].innerHTML = this.editorStyles
		}
	}

	// Watchers
	@Watch("readonly")
	private onReadonlyChanged (readonly: boolean) : void{
		if (this.editor) {
			return
		}

		this.editor!.readonly = !readonly

		if (this.focusWhenReady && !readonly) {
			this.focusWhenReady = false
			this.editor!.focus()
		}
	}

	// Vue lifecycle methods
	private async mounted (): Promise<void> {
		this.setupStyles()
		await this.initialiseHugeRTE()
	}

	private beforeDestroy (): void {
		if (!this.editor) {
			return
		}

		this.currentMargin = {
			top: 25,
			right: 25,
			bottom: 25,
			left: 25
		}

		this.editor = null
		hugerte.remove()
	}
}
